import { useContext, useRef, useState } from 'react'
import { stopSubmit } from 'redux-form'
import _ from 'lodash'
import { Button, Toast, Tip } from '@vzmi/shp_shared_compo'
import UiStateContainer, { defaultUiContextCosumer } from '@vzmi/ec-react-ui-state-container'
import { delay } from '@vzmi/component_ec_nodejs_util/dist/lib/renderUtil'
import { scrollTo } from '../../../../../lib/renderUtil'
import { PAGE_FLOW, UPDATE_LISTING_FORM_KEY } from '../../../../constants/proposalTypes'
import {
  scrollToErrorElement,
  findFormErrorElements,
  FORM_ERROR_MESSAGE
} from '../../../../lib/FormHelper'
import { COMMA } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import { getUploadStateByUI } from '../../../Proposal/Common/ProposalForm/Shared/util'
import {
  FlowContext,
  defaultFailedFlow
} from '../../helper/flow'
import usePageFlow from '../../hook/usePageFlow'
import PreSubmitModal from './PreSubmitModal'
import styles from './index.css'
import { BATCH_PROPOSAL_TYPE } from '../ScheduledBlock'

const fieldParentElements = [
  // 批次賣場展開的卡片內容
  '.ant-collapse-content.ant-collapse-content-active',
  // 單筆(修改賣場資訊)卡片內容
  '[class*="ProposalBlock"]'
]

// 一個欄位通常 40px 加上 20px 瀏覽器頂部間隔
const SCROLL_OFFSET = -60

const ActionFooter = props => {
  const {
    onPendingReview = defaultFailedFlow,
    onComplete = defaultFailedFlow,
    preSubmitContent,
    withPreSubmit = false,
    batchResult,
    dispatch,
    isBatchProposal = true,
    ui
  } = props
  const {
    phase,
    pageFlow,
    setPageFlow,
    isPending,
    setIsPending,
    setIsLoading
  } = useContext(FlowContext)
  const {
    isEditPage,
    isConfirmPage
  } = usePageFlow(phase, pageFlow)
  const callbackRef = useRef(null)
  const [preSubmitModalVisible, setPreSubmitModalVisible] = useState(false)
  // footer 只有修改、確認頁顯示操作提案的按鈕
  const visiblePageFlows = [
    PAGE_FLOW.EDIT,
    PAGE_FLOW.ERROR,
    PAGE_FLOW.CONFIRM
  ]
  const isActionFooterVisible = _.includes(visiblePageFlows, pageFlow)
  if (!isActionFooterVisible) {
    return null
  }
  let isFormPending = isPending
  if (ui) {
    const { isMediaUploading } = getUploadStateByUI(ui)
    isFormPending = (isFormPending || isMediaUploading)
  }

  const onPreSubmit = async (callback) => {
    // 表單含有錯誤的欄位，會在多個 selector 中找到 error message class
    const formFieldErrorElementsList = (await Promise.all(
      _.map(fieldParentElements, findFormErrorElements)
    )).filter(eles => !_.isEmpty(eles))

    const formFieldErrorElement = _.get(
      _.first(formFieldErrorElementsList),
      '0',
      _.first(formFieldErrorElementsList)
    )
    if (_.isElement(formFieldErrorElement)) {
      // 移至錯誤位置並停止 submit 提案
      scrollToErrorElement(SCROLL_OFFSET)
      Toast.error(FORM_ERROR_MESSAGE)
      return
    }

    if (withPreSubmit) {
      callbackRef.current = callback
      setPreSubmitModalVisible(true)
      return
    }

    callback()
  }

  const onPreSubmitModalAction = isContinueSubmit => {
    setPreSubmitModalVisible(false)
    if (!isContinueSubmit) {
      callbackRef.current = null
      return
    }

    callbackRef.current()
  }

  // 完成提案、送出提案前執行
  const beforeProposalSubmit = () => {
    setIsPending(true)
    setIsLoading(true)
  }

  // 完成提案、送出提案後執行
  const afterProposalSubmit = (updatedListings, isPassed) => {
    setIsPending(false)
    setIsLoading(false)
    if (!isPassed) {
      return
    }
    scrollTo(0)
    // 送出成功如果是預約回檔批次提案後更新 querystring 成批次編號
    const batchId = _.get(updatedListings, '0.batches[0].id')
    const isScheduleProposal = !_.isUndefined(batchId)
    console.log({ updatedListings, batchId })
    if (isScheduleProposal) {
      const qs = `batchId=${batchId}&type=${BATCH_PROPOSAL_TYPE.EXECUTE}`
      window.history.pushState('', '', `${window.location.pathname}?${qs}`)
      return
    }
    // 送出成功後更新 querystring 成提案編號
    const proposalIds = _.flow(
      () => _.map(updatedListings, 'proposal.id'),
      ids => _.join(ids, COMMA)
    )()
    const queryKey = isBatchProposal ? 'ids' : 'id'
    window.history.pushState('', '', `${window.location.pathname}?${queryKey}=${proposalIds}`)
  }

  const onSubmitError = async (errorMessage) => {
    // 先移置錯誤的位置再顯示 toast 錯誤訊息，否則有機會 toast 不會跳出
    await scrollToErrorElement(SCROLL_OFFSET)
    errorMessage && Toast.error(errorMessage)
  }

  const setErrorFields = async (errors) => {
    // 指定欄位需要標出錯誤訊息
    // errors = { [field]: { [path]: { [from]: { [form]: 'error message' } } } }
    if (_.isEmpty(errors)) {
      return
    }
    dispatch(stopSubmit(UPDATE_LISTING_FORM_KEY, errors))
    await delay(10)
  }

  const onProposalComplete = () => onPreSubmit(async () => {
    beforeProposalSubmit()
    // 批次系列送出的錯誤訊息通常都直接寫在 PrefixBlock 上
    // 所以 errorMessage 帶過來都是 null，
    // 如果有必要顯示的錯誤訊息再從 onComplete、onPendingReview 回傳
    // errors 用來寫入表單指定欄位需要標出錯誤訊息 EX: 修改規格表
    const [errorMessage, updatedListings, errors] = await onComplete()
    const isPassed = !_.isEmpty(updatedListings)
    afterProposalSubmit(updatedListings, isPassed)
    if (!isPassed) {
      errors && await setErrorFields(errors)
      await onSubmitError(errorMessage)
      return
    }
    setPageFlow(PAGE_FLOW.CONFIRM)
    setErrorFields(errors)
  })

  const onProposalPendingReview = () => onPreSubmit(async () => {
    beforeProposalSubmit()
    const [errorMessage, updatedListings, errors] = await onPendingReview()
    const isPassed = !_.isEmpty(updatedListings)
    afterProposalSubmit(updatedListings, isPassed)
    if (!isPassed) {
      await setErrorFields(errors)
      await onSubmitError(errorMessage)
      return
    }
    setPageFlow(PAGE_FLOW.SUCCESS)
    setErrorFields(errors)
  })

  return (
    <div className={styles.actionFooter}>
      {
        isEditPage && (
          <>
            <Button
              key='submit'
              data-role='complete-proposal-button'
              size='large'
              color='primary'
              disabled={isFormPending}
              onClick={onProposalComplete}
            >
              完成提案
            </Button>
            <Tip
              key='info'
              type='info'
              className={styles.tip}
            >
              完成提案後，需確認資訊才會送出審核
            </Tip>
          </>
        )
      }
      {
        isConfirmPage && (
          <>
            <Button
              key='submit'
              data-role='submit-pending-review-proposal-button'
              size='large'
              color='primary'
              disabled={isFormPending}
              onClick={onProposalPendingReview}
            >
              送出提案
            </Button>
            <Tip
              key='info'
              type='info'
              className={styles.tip}
            >
              {
                // 新增、更新提案有失敗
                !_.isEmpty(batchResult.failedProposalMap) && (
                  <>
                    <span>僅送出提案成功之賣場修改</span>
                    <br />
                  </>
                )
              }
              <span>
                送出提案單，即視為已詳閱、了解並同意提案同意書之全部內容，
                <br />
                並應確保所提報及上架之商品符合本平台刊登規範及相關法令、政令之規定；
                <br />
                提案一經送出，內容即不得再行變更。
              </span>
            </Tip>
          </>
        )
      }
      <PreSubmitModal
        visible={preSubmitModalVisible}
        onCancel={() => onPreSubmitModalAction(false)}
        onOk={() => onPreSubmitModalAction(true)}
        title={isConfirmPage ? '送出提案前確認' : '完成提案前確認'}
      >
        {preSubmitContent}
      </PreSubmitModal>
    </div>
  )
}

export const ActionFooterWithUIState = UiStateContainer.createUi(ActionFooter, defaultUiContextCosumer)

export default ActionFooter
